import { CloseFullscreen, OpenInFullRounded } from '@mui/icons-material'
import GroupIcon from '@mui/icons-material/Group'
import { Chip, IconButton, Paper, Stack, Tooltip, Typography, useTheme } from '@mui/material'
import { useEffect } from 'react'
import { useDispatch } from 'react-redux'
import ApiClient from '../../backend/ApiClient'
import { ITosExpense } from '../../definitions/ServiceExpense'
import { useAppSelector } from '../../store/Hooks'
import { overwriteRouteParams } from '../../store/slices/SessionSlice'
import { ChartDataWithUnit } from '../charts/ChartsHelper'
import { HorizontalBarChartResult } from '../charts/HorizontalBarChartResult'
import LabeledChart from '../controls/data_visualization/LabeledChart'
import useQuery, { buildQueryKey } from '../hooks/useQuery'
import CustomIcon from '../icons/CustomIcon'
import { IMenuItem, IRouteParameters } from '../routes/ContractDetails'
import RoutingManager from '../routing/Routing'

interface P {
    contractId: number
    title: string
    subtitle?: string
    startDate: string
    endDate: string
    icon?: string
    changeView?: (view: string) => void
    triggerSnackbarError?: (view: string) => void
    updateDataFlag: boolean
    setExportMenu: (items: IMenuItem[]) => void
}

function TotalExpenseByTosSection({ contractId, title, subtitle, startDate, endDate, icon, changeView, triggerSnackbarError, updateDataFlag, setExportMenu }: P) {
    const theme = useTheme()
    const dispatch = useDispatch()
    const params = RoutingManager.getParams<IRouteParameters>(useAppSelector(s => s.session.routeParams))
    const viewMode = params?.dataViewMode

    const { response, status: responseStatus, refetch: refetch } = useQuery<ChartDataWithUnit<'bar', ITosExpense['value'][], string>>({
        queryKey: buildQueryKey('totalExpense', params.contractId ?? contractId, params.startDate ?? startDate, params.endDate ?? endDate, viewMode),
        queryFn: async () => {
            let result
            if (viewMode === 'pmpm') {
                result = await ApiClient.getContractTotalExpensePMPMByPeriod({
                    contractId: params.contractId ?? contractId,
                    startDate: params.startDate ?? startDate,
                    endDate: params.endDate ?? endDate
                })
            } else {
                result = await ApiClient.getContractTotalExpenseByPeriod(
                    {
                        contractId: params?.contractId ?? contractId,
                        startDate: params?.startDate ?? startDate,
                        endDate: params?.endDate ?? endDate
                    }
                )
            }

            return {
                datasets: [
                    {
                        data: result.map(item => item.value),
                        unit: '$',
                        backgroundColor: [theme.palette.info.main, theme.palette.primary.light, theme.palette.primary.main, theme.palette.secondary.main, theme.palette.primary.dark]
                    }
                ],
                labels: result.map(item => item.name)
            }
        },
        cache: false
    })

    const handleChangeView = () => {
        changeView('main')
    }

    if (responseStatus && responseStatus === 'error') triggerSnackbarError('There was an error during data fetch')

    useEffect(() => {
        refetch()
    }, [updateDataFlag])

    useEffect(() => {
        setExportMenu([
            { fileType: 'pdf', dataSource: 'expenseByTos', dataViewMode: viewMode },
            { fileType: 'csv', dataSource: 'expenseByTos', dataViewMode: viewMode },
            { fileType: 'csv', dataSource: 'providerExpenseByTos', dataViewMode: viewMode },
            { fileType: 'link' }
        ])
    }, [])

    return (
        <Paper sx={{ height: '100%', padding: 3 }}>
            <Stack height={420}>
                <Stack sx={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: 2 }}>
                    <Stack direction='row' alignItems='center' gap={1}>
                        { viewMode && (
                            <Chip size='small' variant='outlined' label={viewMode === 'pmpm' ? 'PMPM' : 'Total'} />
                        )}
                        <Stack>
                            <Typography variant='h6'>{title}</Typography>
                            {subtitle && (
                                <Typography variant='body2'>{subtitle}</Typography>
                            )}
                        </Stack>
                    </Stack>
                    <Stack justifyContent='end' direction='row' gap='1'>
                        { viewMode && (
                            <Tooltip arrow title={viewMode === 'pmpm' ? 'Show total expenses view' : 'Show PMPM view'}>
                                <div>
                                    <IconButton
                                        size='small'
                                        onClick={() => {
                                            dispatch(overwriteRouteParams({ ...params, dataViewMode: viewMode === 'pmpm' ? 'default' : 'pmpm' }))
                                            setExportMenu([
                                                { fileType: 'pdf', dataSource: 'expenseByTos', dataViewMode: viewMode === 'pmpm' ? 'default' : 'pmpm' },
                                                { fileType: 'csv', dataSource: 'expenseByTos', dataViewMode: viewMode === 'pmpm' ? 'default' : 'pmpm' },
                                                { fileType: 'csv', dataSource: 'providerExpenseByTos', dataViewMode: viewMode === 'pmpm' ? 'default' : 'pmpm' },
                                                { fileType: 'link' }
                                            ])
                                        }}
                                        disabled={responseStatus === 'loading'}>
                                        {viewMode === 'pmpm' ? <GroupIcon /> : <CustomIcon name='pmpm' />}
                                    </IconButton>
                                </div>
                            </Tooltip>
                        )}
                        { icon && (
                            <IconButton
                                size='small'
                                onClick={() => {
                                    handleChangeView()
                                    setExportMenu(null)
                                    dispatch(overwriteRouteParams({ ...params, analyticsSection: 'main' }))
                                }}>
                                {icon === 'close' ? <CloseFullscreen /> : <OpenInFullRounded />}
                            </IconButton>
                        )}
                    </Stack>
                </Stack>
                <LabeledChart
                    label='Total Rbce'>
                    <HorizontalBarChartResult<ITosExpense['value'][]>
                        data={response}
                        status={responseStatus}
                        theme={theme}
                        refetch={refetch}
                    />
                </LabeledChart>
            </Stack>
        </Paper>

    )
}

export default TotalExpenseByTosSection
